import React, { useState, useEffect } from 'react';
import DeploymentModal from './DeploymentModal';
import Header from './Header';

const ShadowingWindow = () => {
  const [sheetUrl, setSheetUrl] = useState('');
  const [passages, setPassages] = useState([]);
  const [selectedPassage, setSelectedPassage] = useState(null);
  const [selectedSentences, setSelectedSentences] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [deploymentData, setDeploymentData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // localStorage에서 저장된 구글 시트 URL 로드
  useEffect(() => {
    const loadSavedSettings = () => {
      try {
        const savedSettings = localStorage.getItem('user_default_settings');
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          if (parsedSettings.defaultSheetUrl) {
            setSheetUrl(parsedSettings.defaultSheetUrl);
            console.log('✅ 저장된 구글 시트 URL 로드됨:', parsedSettings.defaultSheetUrl);
          }
        }
      } catch (error) {
        console.error('❌ 저장된 설정 로드 실패:', error);
      }
    };

    loadSavedSettings();
  }, []);

  // 구글 시트 URL에서 스프레드시트 ID 추출
  const extractSpreadsheetId = (url) => {
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  };

  // 지문 목록 가져오기
  const fetchPassages = async () => {
    if (!sheetUrl.trim()) {
      setError('구글 시트 URL을 입력해주세요.');
      return;
    }

    const spreadsheetId = extractSpreadsheetId(sheetUrl);
    if (!spreadsheetId) {
      setError('유효한 구글 시트 URL을 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      console.log('Fetching passages for spreadsheet:', spreadsheetId);
      console.log('API URL:', '/api/get-listening-passages');
      
      const requestBody = { spreadsheetId };
      console.log('Request body:', requestBody);
      
      const response = await fetch('/api/get-listening-passages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response text:', errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        
        throw new Error(errorData.details || errorData.error || `HTTP ${response.status}`);
      }

      const responseText = await response.text();
      console.log('Raw response text:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('서버 응답을 파싱할 수 없습니다.');
      }
      
      console.log('Parsed response data:', data);
      
      if (data.success) {
        setPassages(data.passages);
        if (data.passages.length === 0) {
          setError('듣기마스터 시트에 지문이 없습니다.');
        }
      } else {
        throw new Error(data.error || '지문을 가져오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('Complete error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      setError(`지문 가져오기 실패: ${error.message}`);
      setPassages([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 지문 선택
  const selectPassage = (passage) => {
    setSelectedPassage(passage);
    setSelectedSentences([]); // 새 지문 선택시 선택된 문장 초기화
  };

  // 문장 선택/해제
  const toggleSentence = (sentenceIndex) => {
    if (!selectedPassage) return;
    
    const sentence = selectedPassage.sentences[sentenceIndex];
    const sentenceId = `${selectedPassage.id}-${sentenceIndex}`;
    
    const sentenceData = {
      id: sentenceId,
      passageTitle: selectedPassage.title,
      sentence: sentence,
      sentenceIndex
    };

    setSelectedSentences(prev => {
      const exists = prev.find(s => s.id === sentenceId);
      if (exists) {
        // 선택 해제
        return prev.filter(s => s.id !== sentenceId);
      } else if (prev.length < 5) {
        // 5개 미만이면 추가
        return [...prev, sentenceData];
      } else {
        // 5개 초과하면 경고
        alert('최대 5개 문장까지만 선택할 수 있습니다.');
        return prev;
      }
    });
  };

  // 날짜 포맷팅
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('ko-KR', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 텍스트 자르기
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // 선택된 문장들로 쉐도잉 생성
  const generateShadowing = async () => {
    if (selectedSentences.length === 0) {
      alert('쉐도잉할 문장을 최소 1개 선택해주세요.');
      return;
    }

    setIsGenerating(true);
    
    try {
      // 선택된 문장들로 가상의 passage 객체 생성
      const customPassage = {
        id: 'custom',
        title: '선택된 문장 쉐도잉',
        content: selectedSentences.map(s => s.sentence).join(' '),
        sentences: selectedSentences.map(s => s.sentence)
      };

      const response = await fetch('/api/generate-shadowing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          passage: customPassage,
          sheetUrl: sheetUrl
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setDeploymentData({
          htmlContent: result.html,
          fileName: result.fileName,
          contentType: 'shadowing'
        });
        setShowDeploymentModal(true);
      } else {
        throw new Error(result.error || '쉐도잉 템플릿 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('쉐도잉 생성 오류:', error);
      alert(`쉐도잉 생성 오류: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // 전체 선택 해제
  const clearSelection = () => {
    setSelectedSentences([]);
  };

  // API 테스트 함수
  const testApiConnection = async () => {
    console.log('Testing API connection...');
    try {
      const response = await fetch('/api/get-listening-passages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ spreadsheetId: 'test123' }),
      });
      
      console.log('Test response status:', response.status);
      const text = await response.text();
      console.log('Test response:', text);
    } catch (error) {
      console.error('API test error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header 컴포넌트 */}
      <Header />
      
      {/* 페이지 제목 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <h1 className="text-2xl font-bold text-gray-900">🎯 쉐도잉 학습 생성기</h1>
            <p className="text-sm text-gray-600 mt-1">구글 시트에서 지문을 가져와 문장을 선택하여 쉐도잉 학습을 만들어보세요</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* URL 입력 섹션 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">구글 시트 URL 입력</h2>
          <div className="flex gap-4">
            <input
              type="url"
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
            <button
              onClick={fetchPassages}
              disabled={isLoading || !sheetUrl.trim()}
              className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {isLoading ? '조회 중...' : '지문 조회'}
            </button>
          </div>
          {error && (
            <div className="mt-3 text-sm text-red-600 bg-red-50 p-3 rounded-md">
              {error}
              <details className="mt-2">
                <summary className="cursor-pointer text-xs">디버그 정보</summary>
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
                  Current URL: {window.location.href}
                  {'\n'}API Endpoint: /api/get-listening-passages
                  {'\n'}Spreadsheet ID: {extractSpreadsheetId(sheetUrl)}
                </pre>
              </details>
            </div>
          )}
        </div>

        {/* 지문 선택 및 문장 선택 섹션 (2칼럼) */}
        {passages.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* 왼쪽: 지문 목록 */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">
                  지문 목록 ({passages.length}개)
                </h2>
              </div>
              
              <div className="p-6 max-h-96 overflow-y-auto">
                <div className="space-y-4">
                  {passages.map((passage) => (
                    <div
                      key={passage.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedPassage?.id === passage.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => selectPassage(passage)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-800 truncate">
                          {passage.title || '제목 없음'}
                        </h3>
                        <span className="text-xs text-gray-500 ml-2">
                          {formatDate(passage.생성일시)}
                        </span>
                      </div>
                      
                      <div className="flex gap-2 mb-2">
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          {passage.학교급} {passage.학년}학년
                        </span>
                        <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded">
                          {passage.지문길이}자
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600">
                        {truncateText(passage.지문내용)}
                      </p>
                      
                      {passage.주제 && (
                        <p className="text-xs text-gray-500 mt-1">
                          주제: {passage.주제}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 오른쪽: 문장 선택 */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-800">
                    문장 선택 ({selectedSentences.length}/5)
                  </h2>
                  {selectedSentences.length > 0 && (
                    <button
                      onClick={clearSelection}
                      className="text-sm text-red-600 hover:text-red-800 transition-colors"
                    >
                      선택 초기화
                    </button>
                  )}
                </div>
              </div>
              
              <div className="p-6 max-h-96 overflow-y-auto">
                {selectedPassage ? (
                  <div className="space-y-3">
                    <div className="mb-4 p-3 bg-purple-50 rounded-md">
                      <h3 className="font-medium text-purple-800">{selectedPassage.지문제목}</h3>
                      <p className="text-sm text-purple-600 mt-1">
                        {selectedPassage.학교급} {selectedPassage.학년}학년 | {selectedPassage.주제}
                      </p>
                    </div>
                    
                    {selectedPassage.sentences.map((sentence, sentenceIndex) => {
                      const sentenceId = `${selectedPassage.id}-${sentenceIndex}`;
                      const isSelected = selectedSentences.some(s => s.id === sentenceId);
                      
                      return (
                        <label
                          key={sentenceIndex}
                          className={`flex items-start gap-3 p-3 rounded-md cursor-pointer transition-colors ${
                            isSelected 
                              ? 'bg-purple-50 border-2 border-purple-200' 
                              : 'hover:bg-gray-50 border-2 border-transparent'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSentence(sentenceIndex)}
                            className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                          <div className="flex-1">
                            <span className="text-xs text-gray-500 font-mono">
                              #{sentenceIndex + 1}
                            </span>
                            <p className="text-gray-900 leading-relaxed">
                              {sentence}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>왼쪽에서 지문을 선택하세요</p>
                    <p className="text-sm mt-1">선택한 지문의 문장들이 여기에 표시됩니다</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 선택된 문장 미리보기 */}
        {selectedSentences.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">선택된 문장 미리보기</h2>
            <div className="space-y-3">
              {selectedSentences.map((item, index) => (
                <div key={item.id} className="flex items-start gap-3 p-3 bg-blue-50 rounded-md">
                  <span className="text-xs text-blue-600 font-mono mt-1">
                    #{index + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm text-blue-800 font-medium">{item.passageTitle}</p>
                    <p className="text-gray-900">{item.sentence}</p>
                  </div>
                  <button
                    onClick={() => setSelectedSentences(prev => prev.filter(s => s.id !== item.id))}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 생성 버튼 */}
        {selectedSentences.length > 0 && (
          <div className="text-center">
            <button
              onClick={generateShadowing}
              disabled={isGenerating}
              className="px-8 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-lg font-semibold flex items-center gap-2 mx-auto"
            >
              {isGenerating && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              )}
              {isGenerating ? '생성 중...' : '🎯 쉐도잉 학습 생성하기'}
            </button>
            <p className="text-sm text-gray-600 mt-2">
              선택된 {selectedSentences.length}개 문장으로 쉐도잉 학습을 생성합니다
            </p>
          </div>
        )}
      </div>

      {/* DeploymentModal */}
      {showDeploymentModal && deploymentData && (
        <DeploymentModal
          isOpen={showDeploymentModal}
          htmlContent={deploymentData.htmlContent}
          fileName={deploymentData.fileName}
          contentType={deploymentData.contentType}
          onClose={() => setShowDeploymentModal(false)}
        />
      )}
    </div>
  );
};

export default ShadowingWindow;